import React, { useEffect, useState } from 'react';
import { FiAward, FiCalendar, FiCheckCircle, FiCreditCard, FiPhone, FiRefreshCw, FiUser, FiX } from 'react-icons/fi';
import api from '../services/api';
import './FootballPredictionPage.css';

function FootballPredictionPage() {
  const [registered, setRegistered] = useState(Boolean(localStorage.getItem('footballToken')));
  const [form, setForm] = useState({ name: '', nationalId: '', phone: '' });
  const [matches, setMatches] = useState([]);
  const [pendingPrediction, setPendingPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const loadMatches = async () => {
    setLoading(true);
    setMessage('');
    try {
      const data = await api.getFootballMatches();
      setMatches(data);
    } catch (err) {
      if (err.status === 401) {
        api.clearFootballToken();
        setRegistered(false);
      }
      setMessage(err.message || '載入賽事失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (registered) loadMatches();
  }, [registered]);

  const handleRegister = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage('');
    try {
      await api.footballRegister(form);
      setRegistered(true);
      setForm({ name: '', nationalId: '', phone: '' });
    } catch (err) {
      setMessage(err.message || '報名失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPrediction = async () => {
    if (!pendingPrediction) return;
    setSubmitting(true);
    setMessage('');
    try {
      await api.predictFootballMatch(pendingPrediction.match.id, pendingPrediction.team.id);
      setPendingPrediction(null);
      await loadMatches();
      setMessage('預測已送出');
    } catch (err) {
      setMessage(err.message || '送出預測失敗');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="world-cup-page">
      <div className="world-cup-container">
        <header className="world-cup-hero">
          <div className="world-cup-kicker">2026 FIFA WORLD CUP</div>
          <h1 className="world-cup-title">2026 足球世界盃活動</h1>
          <p className="world-cup-subtitle">選擇每場賽事的勝利隊伍，賽後依後台結算派發優惠金。</p>
        </header>

        <main className="world-cup-content">
        {!registered ? (
          <form className="world-cup-register" onSubmit={handleRegister}>
            <h2>立即參加</h2>
            <label><FiUser />姓名</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="請輸入真實姓名" />
            <label><FiCreditCard />身分證字號</label>
            <input value={form.nationalId} onChange={e => setForm({ ...form, nationalId: e.target.value.toUpperCase() })} placeholder="例：A123456789" maxLength={10} />
            <label><FiPhone />手機號碼</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="例：0912345678" maxLength={10} />
            {message && <div className="football-message error">{message}</div>}
            <button type="submit" disabled={submitting}>{submitting ? '送出中...' : '立即參加'}</button>
          </form>
        ) : (
          <section className="world-cup-matches">
            <div className="section-header">
              <div>
                <h2 className="section-title">開放預測</h2>
                <div className="section-desc">選擇你支持的勝利隊伍</div>
              </div>
              <button type="button" className="refresh-btn" onClick={loadMatches} disabled={loading}><FiRefreshCw />重新整理</button>
            </div>
            {message && <div className="football-message">{message}</div>}
            {loading ? <div className="football-empty">載入賽事中...</div> : null}
            {!loading && matches.length === 0 ? <div className="football-empty">目前尚未開放賽事</div> : null}
            <div className="match-list">
              {matches.map(match => <MatchCard key={match.id} match={match} submitting={submitting} onPick={team => setPendingPrediction({ match, team })} />)}
            </div>
          </section>
        )}
        </main>
      </div>

      {pendingPrediction && (
        <PredictionConfirmModal
          prediction={pendingPrediction}
          submitting={submitting}
          onCancel={() => setPendingPrediction(null)}
          onConfirm={confirmPrediction}
        />
      )}
    </div>
  );
}

function MatchCard({ match, submitting, onPick }) {
  const selectedTeamId = match.prediction?.selectedTeamId;
  const locked = match.status !== 'open' || Boolean(selectedTeamId) || submitting;

  return (
    <article className="match-card">
      <div className="match-top">
        <span className="match-date"><FiCalendar />{new Date(match.matchAt).toLocaleString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
        <strong className={`match-status ${match.status}`}>{statusText(match.status)}</strong>
      </div>
      <div className="teams-row">
        <TeamButton team={match.teamA} reward={match.teamARewardPerPoint} selected={selectedTeamId === match.teamA.id} winner={match.winnerTeamId === match.teamA.id} disabled={locked} onClick={() => onPick(match.teamA)} />
        <div className="vs-badge">VS</div>
        <TeamButton team={match.teamB} reward={match.teamBRewardPerPoint} selected={selectedTeamId === match.teamB.id} winner={match.winnerTeamId === match.teamB.id} disabled={locked} onClick={() => onPick(match.teamB)} />
      </div>
      <PredictionNote match={match} />
    </article>
  );
}

function TeamButton({ team, reward, selected, winner, disabled, onClick }) {
  return (
    <button type="button" className={`team-card${selected ? ' selected' : ''}${winner ? ' winner' : ''}`} disabled={disabled} onClick={onClick}>
      <TeamFlag team={team} />
      <span className="team-name">{team.name}</span>
      <span className="team-code">{team.countryCode}</span>
      <span className="team-reward"><FiAward /><strong>{money(reward)}</strong> 元優惠金</span>
    </button>
  );
}

function PredictionConfirmModal({ prediction, submitting, onCancel, onConfirm }) {
  const reward = prediction.team.id === prediction.match.teamA.id ? prediction.match.teamARewardPerPoint : prediction.match.teamBRewardPerPoint;
  return (
    <div className="football-confirm-overlay" onMouseDown={onCancel}>
      <div className="football-confirm-modal" onMouseDown={event => event.stopPropagation()}>
        <button type="button" className="football-confirm-close" onClick={onCancel}><FiX /></button>
        <h2>確認預測隊伍</h2>
        <div className="football-confirm-team">
          <TeamFlag team={prediction.team} />
          <strong>{prediction.team.name}</strong>
          <span>{money(reward)} 元優惠金</span>
        </div>
        <p>送出後此場賽事無法重複預測或修改。</p>
        <div className="football-confirm-actions">
          <button type="button" className="secondary" onClick={onCancel} disabled={submitting}>取消</button>
          <button type="button" onClick={onConfirm} disabled={submitting}>{submitting ? '送出中...' : '確認送出'}</button>
        </div>
      </div>
    </div>
  );
}

function TeamFlag({ team }) {
  if (team.flagUrl) return <img className="team-flag" src={team.flagUrl} alt={`${team.name}國旗`} loading="lazy" />;
  return <span className="team-flag team-flag-fallback">{team.flagEmoji}</span>;
}

function PredictionNote({ match }) {
  if (!match.prediction) return <div className="predicted-message muted">每位用戶每場賽事限預測一次</div>;
  const teamName = match.prediction.selectedTeamId === match.teamA.id ? match.teamA.name : match.teamB.name;
  if (match.prediction.resultStatus === 'won') return <div className="predicted-message"><FiCheckCircle />已預測 {teamName}，獲得優惠金 <span className="money">{money(match.prediction.rewardAmount)} 元</span></div>;
  if (match.prediction.resultStatus === 'lost') return <div className="predicted-message muted"><FiCheckCircle />已預測 {teamName}，本場未獲得優惠金</div>;
  return <div className="predicted-message"><FiCheckCircle />已預測 {teamName}，等待賽事結算</div>;
}

function statusText(status) {
  return { open: '開放預測', closed: '已關閉', settled: '已結算' }[status] || status;
}

function money(value) {
  return Number(value || 0).toLocaleString('zh-TW', { maximumFractionDigits: 2 });
}

export default FootballPredictionPage;
